import React from 'react';
import { Link } from 'react-router-dom';
import {
  APP_NAME,
  APP_TAGLINE,
  APP_DESCRIPTION,
  APP_FEATURES,
  APP_AUDIENCE,
  POWERED_BY,
} from '../../legal/appMarketingConstants';

const logoSrc = `${process.env.PUBLIC_URL || ''}/images/workloop-logo.png`;

const AppMarketingPage = () => {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      <header className="border-b border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <span className="text-lg font-semibold text-primary-600 dark:text-primary-400">
            {APP_NAME}
          </span>
          <nav className="flex flex-wrap items-center gap-4 text-sm">
            <Link
              to="/privacy"
              className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms"
              className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
            >
              Terms
            </Link>
            <Link
              to="/support"
              className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
            >
              Support
            </Link>
            <Link
              to="/login"
              className="font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300"
            >
              Sign in
            </Link>
            <Link
              to="/register"
              className="inline-flex items-center rounded-lg bg-primary-600 px-3 py-1.5 font-medium text-white hover:bg-primary-700 transition-colors"
            >
              Get started
            </Link>
          </nav>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="relative overflow-hidden border-b border-gray-200 dark:border-gray-700 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white">
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
              backgroundSize: '40px 40px',
            }}
          />
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24 lg:py-28">
            <div className="max-w-3xl">
              <div className="mb-8 flex items-center gap-4">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white rounded-2xl flex items-center justify-center shadow-xl p-3">
                  <img
                    src={logoSrc}
                    alt={`${APP_NAME} logo`}
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
                <div>
                  <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
                    {APP_NAME}
                  </h1>
                  <p className="mt-1 text-lg sm:text-xl text-primary-100">{APP_TAGLINE}</p>
                </div>
              </div>
              <p className="text-lg sm:text-xl text-primary-50 leading-relaxed max-w-2xl">
                {APP_DESCRIPTION}
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link
                  to="/register"
                  className="inline-flex items-center rounded-xl bg-white px-6 py-3 text-base font-semibold text-primary-700 shadow-lg hover:bg-primary-50 transition-colors"
                >
                  Create free account
                </Link>
                <Link
                  to="/login"
                  className="inline-flex items-center rounded-xl border-2 border-white/80 px-6 py-3 text-base font-semibold text-white hover:bg-white/10 transition-colors"
                >
                  Sign in
                </Link>
              </div>
              <p className="mt-8 text-sm text-primary-200">
                Powered by <span className="font-semibold text-white">{POWERED_BY}</span>
              </p>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <div className="max-w-2xl mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white tracking-tight">
              Everything your team needs to stay connected
            </h2>
            <p className="mt-4 text-lg text-gray-600 dark:text-gray-400">
              From daily chat to task tracking and company-wide announcements, WorkLoop brings
              collaboration tools into one secure workspace.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {APP_FEATURES.map((feature) => (
              <article
                key={feature.title}
                className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 shadow-sm hover:shadow-md transition-shadow"
              >
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                  {feature.description}
                </p>
              </article>
            ))}
          </div>
        </section>

        {/* Audience */}
        <section className="border-y border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
            <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
              <div>
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white tracking-tight">
                  Built for modern teams
                </h2>
                <p className="mt-4 text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
                  {APP_AUDIENCE}
                </p>
              </div>
              <ul className="mt-10 lg:mt-0 space-y-4">
                {[
                  'Corporate and remote teams',
                  'Project-based collaboration',
                  'Admin-managed user access',
                  'Mobile-friendly responsive design',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-1 flex-shrink-0 w-5 h-5 rounded-full bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center">
                      <svg
                        className="w-3 h-3 text-primary-600 dark:text-primary-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </span>
                    <span className="text-gray-700 dark:text-gray-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white tracking-tight">
            Ready to connect your team?
          </h2>
          <p className="mt-4 text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Join WorkLoop today and bring messaging, tasks, and announcements together in one
            platform.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link
              to="/register"
              className="inline-flex items-center rounded-xl bg-primary-600 px-6 py-3 text-base font-semibold text-white hover:bg-primary-700 transition-colors"
            >
              Get started
            </Link>
            <Link
              to="/support"
              className="inline-flex items-center rounded-xl border border-gray-300 dark:border-gray-600 px-6 py-3 text-base font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              Contact support
            </Link>
          </div>
        </section>
      </main>

      <footer className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 flex flex-col sm:flex-row flex-wrap items-center justify-between gap-4 text-sm text-gray-500 dark:text-gray-400">
          <p>
            © {new Date().getFullYear()} {APP_NAME}. Powered by{' '}
            <span className="font-medium text-gray-700 dark:text-gray-300">{POWERED_BY}</span>
          </p>
          <nav className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
            <Link to="/privacy" className="hover:text-primary-600 dark:hover:text-primary-400">
              Privacy Policy
            </Link>
            <Link to="/terms" className="hover:text-primary-600 dark:hover:text-primary-400">
              Terms of Service
            </Link>
            <Link to="/support" className="hover:text-primary-600 dark:hover:text-primary-400">
              Support
            </Link>
          </nav>
        </div>
      </footer>
    </div>
  );
};

export default AppMarketingPage;
